<template>
    <div
        id="chat-wrapper"
        @dragenter="dragEnter"
        @dragleave="dragLeave"
        @dragover="dragOver"
        @drop="dropBlock"
        @paste="handleTPaste"
    >
        <FileDragFrame v-if="dragMode" />
        <template v-else>
            <ChatHeader :contact="contact" v-if="isLive && showHeader" :is-watching="isWatching()" />
            <template v-if="messageSyncRunning">
                <MessageSkeleton />
            </template>
            <template v-else>
                <ChatMessageList :contact="contact" :messages="messages" :messageSyncRunning="messageSyncRunning" />
            </template>
            <ChatMessageArea
                ref="messageArea"
                v-if="isLive"
                :contact="contact"
                :messageSyncRunning="messageSyncRunning"
                :visitorTypingMessage="visitorTypingMessage"
                :media-uploading="uploading"
                @media-message="sendMediaMessage"
            />
        </template>
    </div>
</template>

<script>
import ChatMessageList from "./ChatMessageList.vue";
import ChatHeader from "./ChatHeader.vue";
import { mapActions, mapState } from "vuex";
import ChatMessageArea from "./ChatMessageArea/ChatMessageArea.vue";
import { sendSocketMessage } from "../../../utils/socketStream";
import _ from "lodash";
import axios from "axios";
import mimeTypes from "../../util/mimeTypes";
import { getExtensionFromFileName } from "../../../utils/utils";
import { uploadAsset } from "../../../api/assetUpload";
import FileDragFrame from "./FileDragFrame.vue";
import CONFIG from "../../../config/config";
import { log2 } from "../../../utils/log2";
import MessageSkeleton from "./MessageSkeleton.vue";

const log = log2("Chat");
export default {
    name: "Chat",
    components: { MessageSkeleton, FileDragFrame, ChatMessageArea, ChatHeader, ChatMessageList },
    props: {
        contact: {
            type: Object,
            default: () => ({}),
        },
        isLive: {
            type: Boolean,
            default: false,
        },
        messageSyncRunning: {
            type: Boolean,
            default: false,
        },
        messages: {
            type: Array,
            default: () => [],
        },
        visitorTypingMessage: {
            type: String,
            default: null,
        },
        showHeader: {
            type: Boolean,
            default: true,
        },
    },
    computed: {
        ...mapState({
            agentFileSendEnabled: (state) => _.get(state, "setting.settings.agentFileSendEnabled", true),
            replyToMessages: (state) => state.chat.pinnedReplyToMessages,
            site: (state) => state.site.site,
            user: (state) => state.auth.user,
            watchingContactIds: (state) => state.chat.watchingContactIds,
        }),
    },
    data: () => ({
        dragMode: false,
        uploading: {
            enabled: false,
            percentage: 0,
        },
    }),
    methods: {
        ...mapActions({
            unpinReplyToMessage: "chat/unpinReplyToMessage",
            setLoadingState: "chat/setLoadingState",
            getLoadingState: "chat/getLoadingState",
            getLoadingFilePercent: "chat/getLoadingFilePercent",
        }),
        isWatching() {
            return this.watchingContactIds.includes(this.contact._id);
        },
        dragEnter() {
            if (this.isLive) this.dragMode = true;
        },
        dragLeave() {
            if (this.isLive) this.dragMode = false;
        },
        async dropBlock(ev) {
            if (!this.isLive) return;
            this.dragMode = false;
            ev.preventDefault();
            if (!this.agentFileSendEnabled) {
                return this.$message.error(this.$t("message.dndBlocked"));
            }
            const files = this.$_.get(ev, "dataTransfer.files");
            for (let i = 0; i < (files || []).length; i++) {
                let uploadFile = {
                    chat_id: this.contact?.lastConversation?._id,
                    fileNumber: Math.random(),
                    finished: false,
                    percent: 0,
                    failed: false,
                };
                await this.uploadFile(files[i], uploadFile);
                ev.stopPropagation();
            }
        },
        dragOver(e) {
            if (this.isLive) e.preventDefault();
        },
        async uploadFile(file, uploadFile) {
            if (!this.isLive) return;
            const fileType = getExtensionFromFileName(file.name);

            let path = `${this.site._id}/file/${this.contact?.lastConversation?._id}`;

            let { data } = await uploadAsset({
                path,
                mime: file.type,
                name: file.name,
                ext: mimeTypes[file.type] || fileType || "supsis",
                platform: this.contact.lastConversation.ref,
            });
            if (file.size <= CONFIG.MAX_FILE_UPLOAD_SIZE * 1024 * 1024) {
                const conversationId = this.contact?.lastConversation?._id;
                const socketFeedbackMessage = {
                    conversationId: conversationId,
                    userId: this.user._id,
                    username: this.user.name,
                    siteId: this.site._id,
                    asset: data.asset,
                    channelId: _.get(this.contact, "lastConversation.channel", null),
                    whatsappNumber: _.get(this.contact, "lastConversation.whatsappNumber", null),
                    instagramUserId: _.get(this.contact, "lastConversation.instagramUserId", null),
                    messengerUserId: _.get(this.contact, "lastConversation.messengerUserId", null),
                    telegramUserId: _.get(this.contact, "lastConversation.telegramUserId", null),
                    replyToMessage: _.get(this.replyToMessages, `${this.contact?.lastConversation?._id}.raw`),
                };
                await axios({
                    method: data.method,
                    url: data.url,
                    data: file,
                    headers: {
                        "Content-Type": file.type,
                    },
                    onUploadProgress: await function (progressEvent) {
                        const percent = parseInt(Math.round((progressEvent.loaded / progressEvent.total) * 100));
                        uploadFile.percent = percent;
                        this.setLoadingState(uploadFile);

                        this.getLoadingFilePercent(conversationId).then((percent) => {
                            this.uploading.percentage = percent;
                            this.uploading.enabled = !!percent;
                        });
                    }.bind(this),
                })
                    .then((result) => {
                        this.uploading.enabled = false;
                        uploadFile.finished = true;
                        this.uploading.percentage = 0;
                        this.setLoadingState(uploadFile);
                        this.getLoadingState(uploadFile.fileNumber).then((result) => {
                            sendSocketMessage("user media message", socketFeedbackMessage);
                        });
                        this.unpinReplyToMessage(this.contact?.lastConversation?._id);
                    })
                    .catch((err) => {
                        this.uploading.enabled = false;
                        uploadFile.failed = true;
                        this.setLoadingState(uploadFile);
                    });
            } else {
                log.warn("input is not suitable for send");
                this.$toasted.error(this.$t("message.assetOversizeMB", { mb: CONFIG.MAX_FILE_UPLOAD_SIZE }), {
                    duration: 3000,
                    position: "bottom-center",
                });
            }
        },
        async handleTPaste(event) {
            if (!this.isLive) return;
            const files = _.get(event, "clipboardData.files");
            if (files && files.length > 0) {
                event.preventDefault();
                if (!this.agentFileSendEnabled) {
                    return this.$message.error(this.$t("message.dndBlocked"));
                }
                const chat_id = this.contact?.lastConversation?._id;
                for (let i = 0; i < files.length; i++) {
                    let uploadFile = {
                        chat_id: chat_id,
                        fileNumber: Math.random(),
                        finished: false,
                        percent: 0,
                        failed: false,
                    };
                    await this.uploadFile(files[i], uploadFile);
                    event.stopPropagation();
                }
                return true;
            }
        },
        sendMediaMessage(file) {
            this.uploadFile(file, {
                chat_id: this.contact?.lastConversation?._id,
                fileNumber: Math.random(),
                finished: false,
                percent: 0,
                failed: false,
            });
        },
    },
};
</script>
<style lang="scss" scoped>
#chat-wrapper {
    position: relative;
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    overflow: hidden;
}
</style>
